import React, { useCallback } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import {
    Bold, Italic, List, ListOrdered, Quote, Link2, Undo2, Redo2,
    Heading2, Heading3, Minus,
} from "lucide-react";

function ToolbarButton({ active, disabled, onClick, title, children }) {
    return (
        <button
            type="button"
            onMouseDown={e => { e.preventDefault(); onClick(); }}
            disabled={disabled}
            title={title}
            className={`w-8 h-8 flex items-center justify-center rounded transition-colors
                ${active
                    ? "bg-nova-cyan/20 text-nova-cyan"
                    : "text-zinc-400 hover:text-white hover:bg-white/8"
                }
                disabled:opacity-30 disabled:pointer-events-none`}
        >
            {children}
        </button>
    );
}

function Divider() {
    return <div className="w-px h-5 bg-white/10 mx-0.5" />;
}

export default function RichTextEditor({ value, onChange, placeholder = "Napíšte popis…" }) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({
                heading: { levels: [2, 3] },
                bulletList: {},
                orderedList: {},
                blockquote: {},
                horizontalRule: {},
                code: false,
                codeBlock: false,
            }),
            Link.configure({
                openOnClick: false,
                HTMLAttributes: { class: "tiptap-link" },
            }),
            Placeholder.configure({ placeholder }),
        ],
        content: value || "",
        onUpdate: ({ editor }) => onChange(editor.getHTML()),
        editorProps: {
            attributes: { class: "tiptap-prose" },
        },
    });

    const setLink = useCallback(() => {
        if (!editor) return;
        const prev = editor.getAttributes("link").href;
        const url = window.prompt("URL odkazu:", prev || "https://");
        if (url === null) return;
        if (url === "") { editor.chain().focus().unsetLink().run(); return; }
        editor.chain().focus().setLink({ href: url }).run();
    }, [editor]);

    if (!editor) return null;

    return (
        <div className="rounded-xl border border-white/8 bg-nova-black/40 overflow-hidden focus-within:border-nova-cyan/40 transition-colors">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-white/8 bg-nova-surface/30 flex-wrap">
                <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Tučné (Ctrl+B)">
                    <Bold className="w-3.5 h-3.5" />
                </ToolbarButton>
                <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Kurzíva (Ctrl+I)">
                    <Italic className="w-3.5 h-3.5" />
                </ToolbarButton>

                <Divider />

                <ToolbarButton active={editor.isActive("heading", { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Nadpis 2">
                    <Heading2 className="w-3.5 h-3.5" />
                </ToolbarButton>
                <ToolbarButton active={editor.isActive("heading", { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} title="Nadpis 3">
                    <Heading3 className="w-3.5 h-3.5" />
                </ToolbarButton>

                <Divider />

                <ToolbarButton active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Odrážkový zoznam">
                    <List className="w-3.5 h-3.5" />
                </ToolbarButton>
                <ToolbarButton active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Číslovaný zoznam">
                    <ListOrdered className="w-3.5 h-3.5" />
                </ToolbarButton>

                <Divider />

                <ToolbarButton active={editor.isActive("blockquote")} onClick={() => editor.chain().focus().toggleBlockquote().run()} title="Citácia">
                    <Quote className="w-3.5 h-3.5" />
                </ToolbarButton>
                <ToolbarButton active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Horizontálna čiara">
                    <Minus className="w-3.5 h-3.5" />
                </ToolbarButton>

                <Divider />

                <ToolbarButton active={editor.isActive("link")} onClick={setLink} title="Odkaz">
                    <Link2 className="w-3.5 h-3.5" />
                </ToolbarButton>

                <Divider />

                <ToolbarButton active={false} disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()} title="Späť (Ctrl+Z)">
                    <Undo2 className="w-3.5 h-3.5" />
                </ToolbarButton>
                <ToolbarButton active={false} disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()} title="Znova (Ctrl+Y)">
                    <Redo2 className="w-3.5 h-3.5" />
                </ToolbarButton>
            </div>

            {/* Editor area */}
            <EditorContent editor={editor} className="min-h-[180px]" />
        </div>
    );
}
